// Opening move: Lexical editor with section tagging - systematic feature layering
// Built on baseline foundation, now adding Epic 01: Section Tagging capability
// Big play: Each epic adds ONE focused feature with comprehensive tests

import type { JSX } from 'react'
import { useEffect, useRef, useMemo } from 'react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getRoot, $createParagraphNode, $createTextNode } from 'lexical'
import type { EditorState, LexicalEditor } from 'lexical'

import { nodes } from './config/nodes'
import SectionTaggingPlugin, { APPLY_SECTION_TAG_COMMAND, CLEAR_SECTION_TAG_COMMAND } from './plugins/SectionTaggingPlugin'
import SectionBoundaryNavigationPlugin from './plugins/SectionBoundaryNavigationPlugin'
import SectionAffordancesPlugin from './plugins/SectionAffordancesPlugin'
import EditorRefPlugin from './plugins/EditorRefPlugin'
import InitialSelectionPlugin from './plugins/InitialSelectionPlugin'
import StressDataPlugin from './plugins/StressDataPlugin'
import StressAutoLoadPlugin from './plugins/StressAutoLoadPlugin'
import StressLineAnalysisPlugin from './plugins/StressLineAnalysisPlugin'
import StressOverlayPlugin from './plugins/StressOverlayPlugin'
import StressInteractionPlugin from './plugins/StressInteractionPlugin'
import RhymeSchemePlugin from './plugins/RhymeSchemePlugin'
import SectionValidationPlugin from './plugins/SectionValidationPlugin'
import { TextContentChangePlugin } from './plugins/TextContentChangePlugin'
import { $createSectionBlockNode, type SectionType } from './nodes/SectionBlockNode'
import { SECTION_TAG_REGEX, extractSectionName } from '../../utils/sectionUtils'
import { createDefaultStressCoordinatorService } from './services/StressCoordinatorService'
import FeedbackPanel from './FeedbackPanel'
import { EditorPreferencesProvider } from '../../hooks/useEditorPreferences'
import { mapRhythmicStabilityToLevel } from '../../utils/rhyme/stabilityInterpreter'
import type { SectionRhymeSchemes } from '../../types/rhymeScheme'

// Baseline theme - minimal styling for clean rendering
const theme = {
  root: 'lexical-editor baseline-editor',
  paragraph: 'lexical-paragraph',
  text: {
    bold: 'lexical-text-bold',
    italic: 'lexical-text-italic',
    underline: 'lexical-text-underline',
  },
}

export interface BaselineEditorProps {
  initialContent?: string
  placeholder?: string
  className?: string
  onChange?: (content: string) => void
  onLexicalChange?: (lexicalJSON: string) => void // Epic 06: Lexical JSON callback for save pipeline
  onTextContentChange?: (textContent: string) => void // Text content change detection - Lexical best practice
  editable?: boolean
  enableSectionTagging?: boolean // Epic 01: Enable section tagging features
  enableStressData?: boolean // Epic 02: Enable stress data infrastructure
  enableStressOverlay?: boolean // Epic 04: Enable stress mark overlay rendering
  onEditorRef?: (editor: LexicalEditor) => void // EditorRef plugin callback
  onStressPatternChange?: () => void // Epic 07: Callback when stress patterns are modified
  rhythmicStability?: number // Scope 18: Numeric rhythmic_stability (0-1) for stability-aware rhyme rendering
  sectionSchemes?: SectionRhymeSchemes // Scope 21: Per-section-type rhyme scheme configuration
}

/**
 * Baseline Lexical Editor - Progressive feature enhancement
 *
 * Main play: Renders Lexical editor with optional section tagging capability
 * Built on stable baseline foundation, now supporting Epic 01: Section Tagging
 *
 * Features:
 * - RichTextPlugin for basic text editing
 * - HistoryPlugin for undo/redo
 * - AutoFocusPlugin for immediate cursor placement
 * - Optional: Section tagging with toolbar controls (Epic 01)
 * - Optional: Stress data infrastructure with headless storage (Epic 02)
 * - Optional: Stress mark overlay rendering (Epic 04)
 *
 * Big play: Feature flags enable systematic capability rollout
 * Future epics will add interactive stress editing, metaphor tracking, etc.
 */
export default function BaselineEditor({
  initialContent = '',
  placeholder = 'Start writing...',
  className = '',
  onChange,
  onLexicalChange, // Epic 06: Lexical JSON callback for save pipeline
  onTextContentChange, // Text content change detection - Lexical best practice
  editable = true,
  enableSectionTagging = true, // Epic 01: Default enabled for new editor path
  enableStressData = false, // Epic 02: Default disabled (headless infrastructure only)
  enableStressOverlay = false, // Epic 04: Default disabled (read-only rendering)
  onEditorRef, // EditorRef plugin callback
  onStressPatternChange, // Epic 07: Callback when stress patterns are modified
  rhythmicStability, // Scope 18: Numeric rhythmic_stability (0-1) for stability-aware rhyme rendering
  sectionSchemes, // Scope 21: Per-section-type rhyme scheme configuration
}: BaselineEditorProps) {
  // Opening move: Create stress coordinator service if stress data enabled
  // Big play: Use useMemo to instantiate once and avoid recreating on every render
  const stressCoordinator = useMemo(
    () => (enableStressData ? createDefaultStressCoordinatorService() : null),
    [enableStressData]
  )

  // Compute stability level from rhythmicStability prop
  // Map numeric rhythmic_stability (0-1) to numeric stability level (1-10) for rhyme label rendering
  const stabilityLevel = useMemo(
    () => rhythmicStability !== undefined
      ? mapRhythmicStabilityToLevel(rhythmicStability)
      : 5, // Default to level 5 (medium) if no rhythmic stability provided
    [rhythmicStability]
  )

  // Opening move: Configure the composer with enhanced settings
  // Big play: Create initial editor state with auto-tagging if enabled

  const initialConfig = {
    namespace: 'BaselineEditor',
    theme,
    editable,
    nodes, // Now includes SectionBlockNode from Epic 01
    editorState: initialContent ? (() => {
      // Opening move: detect if initialContent is Lexical JSON vs plain text
      // Time to tackle the tricky bit: Versifai roundtrip sends JSON, manual entry sends text
      try {
        const parsed = JSON.parse(initialContent)
        if (parsed.root && Array.isArray(parsed.root.children)) {
          // Edge case check: Lexical rejects empty state initialization
          // When loading a song with no content yet, we get valid JSON but empty root
          if (parsed.root.children.length === 0) {
            // Return undefined to let Lexical create its default empty state
            return undefined
          }

          // Victory lap: Return the JSON string directly - Lexical will parse it
          // Big play: Built-in JSON parsing handles full state tree (sections, formatting, everything)
          return initialContent
        }
      } catch {
        // Not JSON - continue to plain text handling below
        // Main play: Manual entry or legacy data needs line-by-line parsing
      }

      // Plain text initialization path (existing logic)
      return () => {
        const root = $getRoot()
        root.clear()

        if (enableSectionTagging) {
          // Big play: Auto-tag canonical lyrics on load
          $parseAndTagContent(initialContent)
        } else {
          // Baseline: Just create plain paragraphs
          const lines = initialContent.split('\n')
          lines.forEach((line) => {
            const paragraph = $createParagraphNode()
            if (line.trim()) {
              paragraph.append($createTextNode(line))
            }
            root.append(paragraph)
          })
        }
      }
    })() : undefined,
    onError: (error: Error) => {
      console.error('Lexical error:', error)
    },
  }

  // Main play: Handle content changes and convert to plain text AND Lexical JSON
  // Time to tackle the tricky bit: dual callbacks for backward compatibility
  const handleChange = (editorState: EditorState) => {
    // Opening move: fire plain text callback (existing functionality)
    if (onChange) {
      editorState.read(() => {
        const root = $getRoot()
        const text = root.getTextContent()
        onChange(text)
      })
    }

    // Big play: NEW - fire Lexical JSON callback for save pipeline
    // Victory lap: serialize editor state to canonical JSON for persistence
    // CRITICAL: Use editorState.toJSON() for canonical serialization (deterministic key ordering)
    if (onLexicalChange) {
      const lexicalJSON = JSON.stringify(editorState.toJSON())
      onLexicalChange(lexicalJSON)
    }
  }

  // Reset scroll to top after mount — AutoFocusPlugin can scroll the container
  // down to show the cursor, which looks odd on initial load with a sticky toolbar
  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = 0
      }
    })
    return () => cancelAnimationFrame(frame)
  }, [])

  // Render the Lexical composer with optional section tagging
  // Wrap with EditorPreferencesProvider so FeedbackPanel + StressOverlayPlugin share state
  return (
    <EditorPreferencesProvider>
      <div ref={containerRef} className={`baseline-editor-container ${className}`}>
        <LexicalComposer initialConfig={initialConfig}>
          {/* EditorRef plugin: Capture editor instance for external access */}
          {onEditorRef && <EditorRefPlugin onEditorRef={onEditorRef} />}
          {/* Editor toolbar area - two-row layout for stability */}
          {/* Feedback settings on top (infrequent), section actions below (frequent, near content) */}
          <div className="editor-toolbar-area mb-2">
            {/* Row 1: Feedback toggles - settings adjusted occasionally */}
            {(enableStressOverlay || enableSectionTagging) && <FeedbackPanel />}
            {/* Row 2: Section tagging toolbar - primary editing tools, close to content */}
            {enableSectionTagging && editable && <SectionToolbar />}
          </div>
          <div className="editor-inner">
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className="lexical-content-editable focus:outline-none"
                  aria-placeholder={placeholder}
                  placeholder={<div className="lexical-placeholder">{placeholder}</div>}
                />
              }
              ErrorBoundary={LexicalErrorBoundary as never}
            />
            <HistoryPlugin />
            <AutoFocusPlugin />
            <InitialSelectionPlugin />
            {/* Epic 01: Section tagging plugin */}
            {enableSectionTagging && <SectionTaggingPlugin />}
            {/* Epic 01 Follow-up: Section boundary navigation plugin */}
            {enableSectionTagging && <SectionBoundaryNavigationPlugin />}
            {/* Epic 01 Affordances: Mouse-based insertion controls */}
            {enableSectionTagging && editable && <SectionAffordancesPlugin />}
            {/* Epic 02: Stress data infrastructure plugin */}
            {enableStressData && stressCoordinator && (
              <StressDataPlugin coordinator={stressCoordinator} onStressPatternChange={onStressPatternChange} />
            )}
            {/* Epic 05: Automatic stress data loading plugin */}
            {enableStressData && stressCoordinator && (
              <StressAutoLoadPlugin coordinator={stressCoordinator} />
            )}
            {/* Epic 05 Scope 02: Batch line-level stress analysis plugin */}
            {enableStressData && stressCoordinator && (
              <StressLineAnalysisPlugin coordinator={stressCoordinator} />
            )}
            {/* Epic 04: Stress mark overlay rendering plugin */}
            {enableStressOverlay && <StressOverlayPlugin />}
            {/* Epic 07: Stress interaction plugin for context menu overrides */}
            {enableStressOverlay && <StressInteractionPlugin />}
            {/* Rhyme scheme overlay rendering plugin - always mounted, uses internal preferences */}
            <RhymeSchemePlugin stabilityLevel={stabilityLevel} sectionSchemes={sectionSchemes} />
            {/* Section validation plugin - renders validation badges */}
            {enableSectionTagging && <SectionValidationPlugin sectionSchemes={sectionSchemes} />}
            {/* Text content change detection - Lexical best practice */}
            {onTextContentChange && <TextContentChangePlugin onChange={onTextContentChange} />}
            {/* Legacy onChange plugin - kept for backward compatibility */}
            {/* CRITICAL: ignoreSelectionChange=true prevents false dirty detection from cursor/focus changes */}
            {/* The editorState.toJSON() doesn't include selection, but OnChangePlugin still fires on selection */}
            {/* updates which can cause the callback to re-run with "different" state timing */}
            {onChange && <OnChangePlugin onChange={handleChange} ignoreSelectionChange={true} />}
          </div>
        </LexicalComposer>
      </div>
    </EditorPreferencesProvider>
  )
}

/**
 * SectionToolbar - Manual section tagging controls
 *
 * Main play: Renders toolbar buttons for applying section tags manually
 * Big play: Dispatches APPLY_SECTION_TAG_COMMAND when user clicks a section type
 * Time to tackle the tricky bit: Also provides Clear Tag button to unwrap sections
 */
function SectionToolbar(): JSX.Element {
  const [editor] = useLexicalComposerContext()

  const sectionTypes: Array<{ type: SectionType; label: string }> = [
    { type: 'verse', label: 'Verse' },
    { type: 'chorus', label: 'Chorus' },
    { type: 'bridge', label: 'Bridge' },
    { type: 'outro', label: 'Outro' },
    { type: 'intro', label: 'Intro' },
  ]

  const handleSectionTag = (sectionType: SectionType) => {
    editor.dispatchCommand(APPLY_SECTION_TAG_COMMAND, sectionType)
  }

  const handleClearTag = () => {
    editor.dispatchCommand(CLEAR_SECTION_TAG_COMMAND, undefined)
  }

  return (
    <div className="section-toolbar">
      {sectionTypes.map(({ type, label }) => (
        <button
          key={type}
          type="button"
          className="section-tag-button"
          data-section-type={type}
          onClick={() => handleSectionTag(type)}
          aria-label={`Tag as ${label}`}
        >
          {label}
        </button>
      ))}
      {/* Clear Tag button removes section wrapper */}
      <button
        type="button"
        className="section-tag-button section-clear-button"
        onClick={handleClearTag}
        aria-label="Clear section tag"
      >
        Clear Tag
      </button>
    </div>
  )
}

/**
 * Parse content and apply auto-tagging for canonical section headers
 *
 * Time to tackle the tricky bit: Identify [Section] headers and wrap subsequent
 * lines in SectionBlockNode until the next section header
 *
 * EXPORTED for reuse in setContent implementations
 */
// eslint-disable-next-line react-refresh/only-export-components
export function $parseAndTagContent(content: string): void {
  const root = $getRoot()
  const lines = content.split('\n')

  let currentSection: { node: ReturnType<typeof $createSectionBlockNode>; type: SectionType } | null = null

  lines.forEach((line) => {
    const trimmedLine = line.trim()

    // Big play: Check if line is a section header like [Verse 1], [Chorus]
    if (SECTION_TAG_REGEX.test(trimmedLine)) {
      const sectionName = extractSectionName(trimmedLine)
      if (!sectionName) return

      // Main play: Normalize section name to section type
      const sectionType = normalizeSectionType(sectionName)

      // Time to tackle the tricky bit: Create new section block
      currentSection = {
        node: $createSectionBlockNode(sectionType, sectionName),
        type: sectionType,
      }
      root.append(currentSection.node)
    } else {
      // Main play: Add line as paragraph (inside section if active, or root if not)
      const paragraph = $createParagraphNode()
      if (trimmedLine) {
        const textNode = $createTextNode(line)
        paragraph.append(textNode)
      }

      if (currentSection) {
        currentSection.node.append(paragraph)
      } else {
        root.append(paragraph)
      }
    }
  })
}

/**
 * Normalize section name to section type
 *
 * Big play: Convert "Verse 1", "Chorus", "Bridge" to canonical section types
 */
function normalizeSectionType(sectionName: string): SectionType {
  const normalized = sectionName.toLowerCase()

  if (normalized.includes('verse')) return 'verse'
  if (normalized.includes('chorus')) return 'chorus'
  if (normalized.includes('bridge')) return 'bridge'
  if (normalized.includes('outro')) return 'outro'
  if (normalized.includes('intro')) return 'intro'

  // Victory lap: Default to verse if unrecognized
  return 'verse'
}

// Victory lap: Enhanced editor with section tagging - systematic feature layering complete
// Each epic adds ONE focused capability with comprehensive tests and clean rollback
